import {
	DownOutlined,
	ReloadOutlined,
	RightOutlined,
	SyncOutlined,
	WarningFilled
} from '@ant-design/icons';
import { theme } from 'antd';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { credentialsActions } from 'reduxStore/credentials/slice';
import {
	selectCredentialPropagationForSite,
	selectCredentials,
	selectPropagationStatus
} from 'reduxStore/credentials/selectors';
import { selectDevices } from 'reduxStore/devices/selectors';
import { selectPeopleNameMap } from 'reduxStore/people/selectors';
import { Badge, Button, Dropdown, Empty, Flex, Pagination, Progress, Segmented, Tag, Tooltip, Typography } from 'zui/Antd';

dayjs.extend(relativeTime);

const { Text, Title } = Typography;

const FAILED_PAGE_SIZE = 8;
const PANEL_WIDTH = 400;

/* Map a raw external error string to a short, human reason bucket.
   The backend may already return a structured reason; if so, prefer it. */
const reasonBucket = (error) => {
	if (!error) return 'Unknown';
	const e = error.toLowerCase();
	if (e.includes('not connected') || e.includes('gateway') || e.includes('offline')) return 'Gateway offline';
	if (e.includes('slot') || e.includes('full')) return 'Slot full';
	if (e.includes('reject')) return 'Rejected';
	return 'Other';
};

const REASON_TAG_COLOR = {
	'Gateway offline': 'error',
	'Slot full': 'warning',
	Rejected: 'default',
	Other: 'default',
	Unknown: 'default'
};

/* The count is CREDENTIALS AFFECTED by this failure reason — not how many
   gateways/locks have the problem. */
const REASON_EXPLANATION = {
	'Gateway offline': (n) =>
		`${n} credential${n !== 1 ? 's' : ''} couldn't be written because the lock's gateway is offline`,
	'Slot full': (n) => `${n} credential${n !== 1 ? 's' : ''} rejected — the lock has no free credential slots`,
	Rejected: (n) => `${n} credential${n !== 1 ? 's' : ''} rejected by the lock`
};

const ReasonTag = ({ bucket, count }) => (
	<Tooltip
		title={
			REASON_EXPLANATION[bucket]
				? REASON_EXPLANATION[bucket](count)
				: `${count} credential${count !== 1 ? 's' : ''} failed — ${bucket}`
		}
		placement='top'
	>
		<Tag color={REASON_TAG_COLOR[bucket] || 'default'} style={{ marginInlineEnd: 0 }}>
			{bucket} · {count}
		</Tag>
	</Tooltip>
);

/* ================================================================
   ACTIVE TAB — one row per in-flight credential with live progress
   ================================================================ */
const ActiveTabContent = ({ propagation, credentials, peopleNameMap, token, dispatch }) => {
	const entries = Object.entries(propagation).filter(([, e]) => !e.complete || e.failed > 0);

	if (entries.length === 0) {
		return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No credentials syncing' style={{ padding: 24 }} />;
	}

	return (
		<Flex vertical gap={12} style={{ maxHeight: 420, overflow: 'auto', padding: '4px 16px' }}>
			{entries.map(([credentialID, entry]) => {
				const cred = credentials.find((c) => String(c.credentialID) === credentialID);
				const personName = cred?.personID
					? peopleNameMap.get(cred.personID)
					: entry.personID
						? peopleNameMap.get(entry.personID)
						: null;
				const label = cred?.name || cred?.uniqueIdentifier || entry.credentialName || `#${credentialID}`;
				const isQueued = entry.queued;
				const hasFailed = entry.complete && entry.failed > 0;
				const failedCount = entry.results.filter((r) => r.status === 'failed' || r.status === 'skipped').length;
				const percent = entry.total > 0 ? Math.round(((entry.current || 0) / entry.total) * 100) : 0;
				const action = entry.type === 'unassign' ? 'Removing from' : 'Syncing to';
				// No activity for 16+ min → the run died mid-flight; the background
				// sweep reclaims stuck rows after 15 min and resumes it.
				const isStalled =
					!entry.complete && !isQueued && entry._touchedAt && Date.now() - entry._touchedAt > 16 * 60 * 1000;

				return (
					<Flex key={credentialID} vertical gap={4}>
						<Flex align='center' justify='space-between' gap={8}>
							<Flex vertical style={{ minWidth: 0 }}>
								<Text strong ellipsis style={{ fontSize: 13 }}>
									{label}
									{personName ? <Text type='secondary' style={{ fontSize: 12 }}> · {personName}</Text> : null}
								</Text>
								<Text type='secondary' style={{ fontSize: 12 }}>
									{isQueued
										? `Waiting — ${entry.total} device${entry.total !== 1 ? 's' : ''}`
										: `${action} ${entry.total} device${entry.total !== 1 ? 's' : ''}${failedCount ? ` · ${failedCount} failed` : ''}${isStalled ? ' · stalled — resuming via background sweep' : ''}`}
								</Text>
							</Flex>
							{hasFailed && (
								<Button
									size='small'
									icon={<ReloadOutlined />}
									onClick={() =>
										dispatch(
											credentialsActions.retryCredentialPropagation({
												credentialID: Number(credentialID),
												deviceIDs: entry.failedDeviceIDs || []
											})
										)
									}
								>
									Retry
								</Button>
							)}
						</Flex>
						<Progress
							percent={percent}
							size='small'
							status={hasFailed ? 'exception' : entry.complete ? 'success' : 'active'}
							strokeColor={hasFailed ? token.colorError : undefined}
						/>
					</Flex>
				);
			})}
		</Flex>
	);
};

/* ================================================================
   FAILED + ATTENTION TABS — expandable rows, one per credential

   Two severities share the row UI (variant prop):
   - 'failed' → the server sweep will auto-retry these; Retry jumps the queue.
   - 'dead'   → the sweep gave up; ONLY a manual Retry revives them.
                Unassign groups first — credentials still live on a lock.
   ================================================================ */
const FailedTabContent = ({ items, variant = 'failed', devicesMap, peopleNameMap, token, dispatch, setActiveTab }) => {
	const [page, setPage] = useState(1);
	const [expanded, setExpanded] = useState(() => new Set());
	const isDead = variant === 'dead';

	const toggle = useCallback((id) => {
		setExpanded((prev) => {
			const next = new Set(prev);
			if (next.has(id)) next.delete(id);
			else next.add(id);
			return next;
		});
	}, []);

	// Group items by credentialID — one row per credential, not per device
	const grouped = useMemo(() => {
		const map = new Map();
		for (const item of items || []) {
			const key = item.credentialID;
			if (!map.has(key)) {
				map.set(key, {
					credentialID: item.credentialID,
					credentialName: item.credentialName || item.uniqueIdentifier || `#${item.credentialID}`,
					personID: item.personID,
					hasUnassign: false,
					devices: [],
					latestModified: item.modifiedDate
				});
			}
			const group = map.get(key);
			if (item.type === 'unassign') group.hasUnassign = true;
			const deviceInfo = devicesMap?.get(item.deviceID);
			group.devices.push({
				deviceID: item.deviceID,
				deviceName: deviceInfo?.name || item.deviceName || `Device ${item.deviceID}`,
				error: item.error
			});
			if (item.modifiedDate > group.latestModified) group.latestModified = item.modifiedDate;
			// Sweep retry accounting (failed rows only): attempt = furthest device,
			// nextAttemptAt = the soonest re-drive among the group's devices
			if (item.attemptCount != null && item.attemptCount > (group.attemptCount || 0)) {
				group.attemptCount = item.attemptCount;
			}
			if (item.nextAttemptAt && (!group.nextAttemptAt || item.nextAttemptAt < group.nextAttemptAt)) {
				group.nextAttemptAt = item.nextAttemptAt;
			}
		}
		const groups = [...map.values()];
		// Dead unassigns are credentials still live on a lock — surface them first
		if (isDead) {
			groups.sort(
				(a, b) =>
					(a.hasUnassign ? 0 : 1) - (b.hasUnassign ? 0 : 1) ||
					(a.latestModified < b.latestModified ? 1 : -1)
			);
		}
		return groups;
	}, [items, devicesMap, isDead]);

	// Reason summary across all failed credentials — drives the tag row
	const reasonTotals = useMemo(() => {
		const counts = new Map();
		for (const g of grouped) {
			const buckets = new Set(g.devices.map((d) => reasonBucket(d.error)));
			for (const b of buckets) counts.set(b, (counts.get(b) || 0) + 1);
		}
		const order = ['Gateway offline', 'Slot full', 'Rejected', 'Other', 'Unknown'];
		return [...counts.entries()].sort((a, b) => order.indexOf(a[0]) - order.indexOf(b[0]));
	}, [grouped]);

	const totalPages = Math.max(1, Math.ceil(grouped.length / FAILED_PAGE_SIZE));
	useEffect(() => {
		if (page > totalPages) setPage(totalPages);
	}, [page, totalPages]);

	const paginatedItems = grouped.slice((page - 1) * FAILED_PAGE_SIZE, page * FAILED_PAGE_SIZE);

	if (grouped.length === 0) {
		return (
			<Empty
				image={Empty.PRESENTED_IMAGE_SIMPLE}
				description={isDead ? 'Nothing needs attention' : 'No failed credentials'}
				style={{ padding: 24 }}
			/>
		);
	}

	const deadUnassignCount = isDead ? grouped.filter((g) => g.hasUnassign).length : 0;

	return (
		<Flex vertical gap={8}>
			<Flex style={{ padding: '0 16px' }}>
				<Text type='secondary' style={{ fontSize: 12 }}>
					{isDead
						? 'Auto-retry gave up on these (attempts exhausted or terminal error) — only a manual Retry re-drives them.'
						: 'The server auto-retries these in the background (30s → 2m → 10m, 3 attempts), then moves them to Attention. Retry jumps the queue.'}
				</Text>
			</Flex>
			{isDead && deadUnassignCount > 0 && (
				<Flex align='center' gap={6} style={{ padding: '6px 16px', background: token.colorErrorBg }}>
					<WarningFilled style={{ fontSize: 12, color: token.colorError, flexShrink: 0 }} />
					<Text style={{ fontSize: 12, color: token.colorError }}>
						{deadUnassignCount} credential{deadUnassignCount !== 1 ? 's' : ''} still active on locks — removal
						failed permanently. Retry to clear.
					</Text>
				</Flex>
			)}

			{reasonTotals.length > 0 && (
				<Flex wrap gap={4} style={{ padding: '0 16px' }}>
					{reasonTotals.map(([bucket, count]) => (
						<ReasonTag key={bucket} bucket={bucket} count={count} />
					))}
				</Flex>
			)}

			<Flex vertical style={{ maxHeight: 360, overflow: 'auto' }}>
				{paginatedItems.map((group) => {
					const isExpanded = expanded.has(group.credentialID);
					const personName = group.personID ? peopleNameMap.get(group.personID) : null;
					return (
						<Flex key={group.credentialID} vertical>
							<Flex
								align='center'
								justify='space-between'
								gap={8}
								onClick={() => toggle(group.credentialID)}
								style={{ padding: '8px 16px', cursor: 'pointer' }}
							>
								<Flex align='center' gap={8} style={{ minWidth: 0 }}>
									{isExpanded ? (
										<DownOutlined style={{ fontSize: 10, color: token.colorTextSecondary }} />
									) : (
										<RightOutlined style={{ fontSize: 10, color: token.colorTextSecondary }} />
									)}
									<Flex vertical style={{ minWidth: 0 }}>
										<Text strong ellipsis style={{ fontSize: 13 }}>
											{group.credentialName}
											{personName ? (
												<Text type='secondary' style={{ fontSize: 12 }}> · {personName}</Text>
											) : null}
										</Text>
										<Text type='secondary' style={{ fontSize: 12 }}>
											{group.hasUnassign ? 'Removal failed · ' : ''}
											{group.devices.length} device{group.devices.length !== 1 ? 's' : ''}
											{group.latestModified ? ` · ${dayjs(group.latestModified).fromNow()}` : ''}
											{!isDead &&
												(group.nextAttemptAt
													? dayjs(group.nextAttemptAt).isAfter(dayjs())
														? ` · auto-retry ${Math.min((group.attemptCount || 0) + 1, 3)}/3 ${dayjs(group.nextAttemptAt).fromNow()}`
														: ' · auto-retrying now…'
													: ' · queued for auto-retry')}
										</Text>
									</Flex>
								</Flex>
								<Button
									size='small'
									icon={<ReloadOutlined />}
									onClick={(e) => {
										e.stopPropagation();
										dispatch(
											credentialsActions.retryCredentialPropagation({
												credentialID: group.credentialID,
												deviceIDs: group.devices.map((d) => d.deviceID)
											})
										);
										setActiveTab('active');
									}}
								>
									Retry
								</Button>
							</Flex>
							{isExpanded && (
								<Flex vertical gap={4} style={{ padding: '0 16px 8px 34px' }}>
									{group.devices.map((d) => (
										<Flex key={d.deviceID} align='center' justify='space-between' gap={8}>
											<Text ellipsis style={{ fontSize: 12 }}>
												{d.deviceName}
											</Text>
											<Tag color={REASON_TAG_COLOR[reasonBucket(d.error)] || 'default'} style={{ marginInlineEnd: 0 }}>
												{reasonBucket(d.error)}
											</Tag>
										</Flex>
									))}
								</Flex>
							)}
						</Flex>
					);
				})}
			</Flex>

			{grouped.length > FAILED_PAGE_SIZE && (
				<Flex justify='center' style={{ paddingBottom: 4 }}>
					<Pagination
						simple
						size='small'
						current={page}
						pageSize={FAILED_PAGE_SIZE}
						total={grouped.length}
						onChange={setPage}
					/>
				</Flex>
			)}
		</Flex>
	);
};

/* ================================================================
   ROOT — Quick Actions-style trigger + dropdown panel
   ================================================================ */
const PropagationIndicator = () => {
	const {
		token,
		token: { colorBgContainer, borderRadiusLG, boxShadow, colorTextLabel, paddingMD, fontWeightStrong }
	} = theme.useToken();
	const dispatch = useDispatch();

	const propagation = useSelector(selectCredentialPropagationForSite);
	const propagationStatus = useSelector(selectPropagationStatus);
	const credentials = useSelector(selectCredentials);
	const peopleNameMap = useSelector(selectPeopleNameMap);
	const devices = useSelector(selectDevices);

	const [open, setOpen] = useState(false);
	const [activeTab, setActiveTab] = useState('active');

	// Re-render every 30s while open so retry countdowns and stalled
	// detection stay current without any data changing.
	const [, setClockTick] = useState(0);
	useEffect(() => {
		if (!open) return undefined;
		const t = setInterval(() => setClockTick((n) => n + 1), 30000);
		return () => clearInterval(t);
	}, [open]);

	const prevActiveRef = useRef(0);
	const autoOpenedRef = useRef(false);

	const lastFetchRef = useRef(0);
	const fetchPropagationOnce = useCallback(() => {
		const now = Date.now();
		if (now - lastFetchRef.current < 750) return;
		lastFetchRef.current = now;
		dispatch(credentialsActions.fetchPropagation());
	}, [dispatch]);

	const hydratedRef = useRef(false);
	useEffect(() => {
		if (!hydratedRef.current) {
			hydratedRef.current = true;
			fetchPropagationOnce();
		}
	}, [fetchPropagationOnce]);

	// While anything is in flight or awaiting auto-retry, poll the DB state.
	// Sweep re-drives can run on ANOTHER server instance — its socket events
	// never reach this client, so without polling a cross-server run looks
	// frozen (or parked in Failed) until a manual reload.
	const hasLiveWork = useRef(false);
	useEffect(() => {
		const anyInFlight = Object.values(propagation).some((e) => !e.complete);
		hasLiveWork.current = anyInFlight || (propagationStatus || []).some((i) => i.status !== 'dead');
	});
	useEffect(() => {
		const t = setInterval(() => {
			if (hasLiveWork.current) fetchPropagationOnce();
		}, 20000);
		return () => clearInterval(t);
	}, [fetchPropagationOnce]);

	const devicesMap = useMemo(() => {
		const m = new Map();
		(devices || []).forEach((d) => m.set(d.deviceID, { name: d.name, defaultCategoryID: d.defaultCategoryID }));
		return m;
	}, [devices]);

	const activeCount = useMemo(() => Object.values(propagation).filter((e) => !e.complete).length, [propagation]);

	// A credential being re-driven RIGHT NOW (sweep auto-retry or manual) lives
	// in the Active tab only — its failed/dead rows in the DB snapshot are stale
	// until the run ends (COMPLETE refetches). Showing both would offer the user
	// a Retry on something already retrying.
	const inFlightIDs = useMemo(() => {
		const set = new Set();
		for (const [id, entry] of Object.entries(propagation)) {
			if (!entry.complete) set.add(Number(id));
		}
		return set;
	}, [propagation]);

	// The server returns failed + dead rows in one list; 'dead' means the
	// auto-retry sweep gave up (needs a human), 'failed' means it will retry.
	const { failedItems, deadItems } = useMemo(() => {
		const failed = [];
		const dead = [];
		for (const item of propagationStatus || []) {
			if (inFlightIDs.has(item.credentialID)) continue;
			(item.status === 'dead' ? dead : failed).push(item);
		}
		return { failedItems: failed, deadItems: dead };
	}, [propagationStatus, inFlightIDs]);

	const failedCount = failedItems.length ? new Set(failedItems.map((i) => i.credentialID)).size : 0;
	const deadCount = deadItems.length ? new Set(deadItems.map((i) => i.credentialID)).size : 0;
	const badgeCount = activeCount + failedCount + deadCount;

	// New propagation started → open on the Active tab
	useEffect(() => {
		if (activeCount > 0 && prevActiveRef.current === 0 && !open) {
			setOpen(true);
			setActiveTab('active');
			autoOpenedRef.current = true;
		}
		prevActiveRef.current = activeCount;
	}, [activeCount, open]);

	// A run just completed with failures → jump to the Failed tab + refetch DB state
	const prevPropagationRef = useRef(propagation);
	useEffect(() => {
		const prev = prevPropagationRef.current;
		let anyNewlyFailed = false;
		for (const [id, entry] of Object.entries(propagation)) {
			const justCompleted = entry.complete && (!prev[id] || !prev[id].complete);
			if (justCompleted && entry.failed > 0) anyNewlyFailed = true;
		}
		if (anyNewlyFailed) {
			// Preselect Failed only when the popover is closed — yanking the tab
			// from under the user mid-burst made the UI jump around.
			if (!open) setActiveTab('failed');
			fetchPropagationOnce();
		}
		prevPropagationRef.current = propagation;
	}, [propagation, fetchPropagationOnce, open]);

	// Everything cleared after an auto-open → close on a short delay
	useEffect(() => {
		if (activeCount === 0 && failedCount === 0 && deadCount === 0 && open && autoOpenedRef.current) {
			const t = setTimeout(() => {
				setOpen(false);
				autoOpenedRef.current = false;
			}, 3000);
			return () => clearTimeout(t);
		}
	}, [activeCount, failedCount, deadCount, open]);

	const handleOpenChange = useCallback(
		(newOpen) => {
			setOpen(newOpen);
			if (newOpen) {
				autoOpenedRef.current = false;
				fetchPropagationOnce();
			}
		},
		[fetchPropagationOnce]
	);

	const tabOptions = [
		{ value: 'active', label: `Active${activeCount ? ` (${activeCount})` : ''}` },
		{ value: 'failed', label: `Failed${failedCount ? ` (${failedCount})` : ''}` },
		{ value: 'attention', label: `Attention${deadCount ? ` (${deadCount})` : ''}` }
	];

	return (
		<Dropdown
			arrow
			placement='bottom'
			trigger={['click']}
			open={open}
			onOpenChange={handleOpenChange}
			dropdownRender={() => (
				<Flex
					style={{
						backgroundColor: colorBgContainer,
						borderRadius: borderRadiusLG,
						width: PANEL_WIDTH,
						boxShadow: boxShadow,
						padding: `${paddingMD}px 0px ${paddingMD}px`
					}}
					vertical
					gap={12}
				>
					<Title level={5} style={{ textAlign: 'center', margin: 0 }} color={colorTextLabel}>
						Credential Sync
					</Title>

					<Flex justify='center'>
						<Segmented size='small' options={tabOptions} value={activeTab} onChange={setActiveTab} />
					</Flex>

					{activeTab === 'active' && (
						<ActiveTabContent
							propagation={propagation}
							credentials={credentials}
							peopleNameMap={peopleNameMap}
							token={token}
							dispatch={dispatch}
						/>
					)}
					{activeTab === 'failed' && (
						<FailedTabContent
							items={failedItems}
							variant='failed'
							devicesMap={devicesMap}
							peopleNameMap={peopleNameMap}
							token={token}
							dispatch={dispatch}
							setActiveTab={setActiveTab}
						/>
					)}
					{activeTab === 'attention' && (
						<FailedTabContent
							items={deadItems}
							variant='dead'
							devicesMap={devicesMap}
							peopleNameMap={peopleNameMap}
							token={token}
							dispatch={dispatch}
							setActiveTab={setActiveTab}
						/>
					)}
				</Flex>
			)}
		>
			<Badge count={badgeCount} size='small' offset={[-4, 0]}>
				<Button icon={<SyncOutlined spin={activeCount > 0} />} style={{ fontWeight: fontWeightStrong }}>
					Credential Sync
				</Button>
			</Badge>
		</Dropdown>
	);
};

export default PropagationIndicator;
